import React, { useState, useEffect } from 'react';
import ApiService from '../services/ApiService';
import './HashtagSelector.css';

interface Hashtag {
  id: number;
  name: string;
  displayName: string;
  category: string | null;
  isOfficial: boolean;
  userCount: number;
}

interface HashtagSelectorProps {
  selectedHashtags: Hashtag[];
  onHashtagsChange: (hashtags: Hashtag[]) => void;
  maxHashtags?: number;
}

const HashtagSelector: React.FC<HashtagSelectorProps> = ({
  selectedHashtags,
  onHashtagsChange,
  maxHashtags = 10
}) => {
  const [allHashtags, setAllHashtags] = useState<Hashtag[]>([]);
  const [filteredHashtags, setFilteredHashtags] = useState<Hashtag[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [customHashtag, setCustomHashtag] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [loading, setLoading] = useState(true);

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'interests', label: 'Interests' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'looking_for', label: 'Looking For' },
    { value: 'personality', label: 'Personality' },
    { value: 'physical', label: 'Physical' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchHashtags();
  }, []);

  useEffect(() => {
    filterHashtags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchQuery, allHashtags]);

  const fetchHashtags = async () => {
    try {
      const response = await ApiService.get('/hashtags');
      setAllHashtags(response.data.hashtags);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch hashtags:', error);
      setLoading(false);
    }
  };

  const filterHashtags = () => {
    let filtered = allHashtags;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(h => h.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(h =>
        h.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredHashtags(filtered);
  };

  const isSelected = (hashtag: Hashtag) => {
    return selectedHashtags.some(h => h.id === hashtag.id);
  };

  const toggleHashtag = async (hashtag: Hashtag) => {
    if (isSelected(hashtag)) {
      // Remove hashtag
      try {
        await ApiService.delete(`/hashtags/${hashtag.id}`);
        onHashtagsChange(selectedHashtags.filter(h => h.id !== hashtag.id));
      } catch (error) {
        console.error('Failed to remove hashtag:', error);
      }
    } else {
      // Add hashtag
      if (selectedHashtags.length >= maxHashtags) {
        alert(`You can only select up to ${maxHashtags} hashtags`);
        return;
      }
      try {
        await ApiService.post('/hashtags/add', { hashtagName: hashtag.name });
        onHashtagsChange([...selectedHashtags, hashtag]);
      } catch (error) {
        console.error('Failed to add hashtag:', error);
      }
    }
  };

  const addCustomHashtag = async () => {
    if (!customHashtag.trim()) return;

    if (selectedHashtags.length >= maxHashtags) {
      alert(`You can only select up to ${maxHashtags} hashtags`);
      return;
    }

    try {
      const response = await ApiService.post('/hashtags/add', {
        hashtagName: customHashtag
      });
      onHashtagsChange([...selectedHashtags, response.data.hashtag]);
      setCustomHashtag('');
      setShowCustomInput(false);
      // Refresh hashtag list
      fetchHashtags();
    } catch (error: any) {
      console.error('Failed to add custom hashtag:', error);
      alert(error.response?.data?.message || 'Failed to add hashtag');
    }
  };

  if (loading) {
    return <div className="hashtag-selector-loading">Loading hashtags...</div>;
  }

  return (
    <div className="hashtag-selector">
      <div className="hashtag-header">
        <h3>Add Hashtags to Your Profile</h3>
        <p className="hashtag-count">
          {selectedHashtags.length} / {maxHashtags} selected
        </p>
      </div>

      {/* Search and Category Filter */}
      <div className="hashtag-filters">
        <input
          type="text"
          className="hashtag-search"
          placeholder="Search hashtags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <select
          className="hashtag-category-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      {/* Selected Hashtags */}
      {selectedHashtags.length > 0 && (
        <div className="selected-hashtags-section">
          <h4>Your Hashtags:</h4>
          <div className="hashtag-grid">
            {selectedHashtags.map(hashtag => (
              <button
                key={hashtag.id}
                className="hashtag-chip selected"
                onClick={() => toggleHashtag(hashtag)}
              >
                {hashtag.displayName}
                <span className="remove-icon">×</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Available Hashtags */}
      <div className="available-hashtags-section">
        <h4>Browse Hashtags:</h4>
        <div className="hashtag-grid">
          {filteredHashtags
            .filter(h => !isSelected(h))
            .map(hashtag => (
              <button
                key={hashtag.id}
                className={`hashtag-chip ${hashtag.isOfficial ? 'official' : 'custom'}`}
                onClick={() => toggleHashtag(hashtag)}
                title={`${hashtag.userCount} users`}
              >
                {hashtag.displayName}
                {hashtag.userCount > 0 && (
                  <span className="user-count">{hashtag.userCount}</span>
                )}
              </button>
            ))}
        </div>
      </div>

      {/* Custom Hashtag Input */}
      <div className="custom-hashtag-section">
        {!showCustomInput ? (
          <button
            className="add-custom-button"
            onClick={() => setShowCustomInput(true)}
          >
            + Create Custom Hashtag
          </button>
        ) : (
          <div className="custom-input-container">
            <input
              type="text"
              className="custom-hashtag-input"
              placeholder="Enter custom hashtag (no # needed)"
              value={customHashtag}
              onChange={(e) => setCustomHashtag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCustomHashtag()}
            />
            <button className="add-button" onClick={addCustomHashtag}>
              Add
            </button>
            <button
              className="cancel-button"
              onClick={() => {
                setShowCustomInput(false);
                setCustomHashtag('');
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HashtagSelector;
